import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowUpRight, MoreHorizontal, Phone, Trash2 } from "lucide-react";
import { convertLead, deleteLead, type Lead } from "../../../lib/clients";
import { Pop, useClosePops } from "../../shell/Pop";
import { useCallContext } from "../../telephony/CallProvider";
import { ConfirmModal } from "../_shared/ConfirmModal";
import type { T } from "./shared";

/**
 * The 44px column at the end of a lead row.
 *
 * The same three entries the board card carries, in the app's own menu rather
 * than in the card's hand-rolled one: open the record, ring it, turn it into a
 * client, delete it. The call entry is first because that is what this list is
 * for - a lead is a company somebody still has to ring.
 *
 * Must be rendered inside a `PopHost`.
 *
 * `LeadRowActions` and not `LeadRowMenu` for the reason `LeadsList` is not
 * `LeadsTable`: `sections/scan-leads/` holds a `LeadRowMenu.tsx` of its own, for
 * a different screen that shares the word "lead".
 */
export function LeadRowActions({
  lead,
  t,
  onDelete,
  onConvert,
}: {
  lead: Lead;
  t: T;
  onDelete: (id: string) => void;
  onConvert: (id: string) => void;
}) {
  const nav = useNavigate();
  const close = useClosePops();
  const call = useCallContext();
  const canCall = Boolean(call?.enabled) && Boolean(lead.phone);
  const [confirmDel, setConfirmDel] = useState(false);

  async function doConvert() {
    close();
    await convertLead(lead.id);
    onConvert(lead.id);
  }

  async function doDelete() {
    await deleteLead(lead.id);
    onDelete(lead.id);
  }

  return (
    // Above the row-covering link overlay, or the menu would never get the click.
    <span className="relative z-[1] justify-self-end">
      <Pop
        trigger={(open) => (
          <button
            type="button"
            className={`crm-cb${open ? " is-on" : ""}`}
            title={t("common.more")}
            aria-label={t("common.more")}
            aria-haspopup="menu"
            aria-expanded={open}
          >
            <MoreHorizontal className="size-4" />
          </button>
        )}
      >
        <button
          type="button"
          className="crm-mi"
          onClick={() => {
            close();
            nav(`/leads/${lead.id}`);
          }}
        >
          <ArrowUpRight className="size-4" />
          {t("leads.openLead")}
        </button>
        {canCall ? (
          <button
            type="button"
            className="crm-mi"
            onClick={() => {
              close();
              call?.startCall({
                phone: lead.phone!,
                companyName: lead.name,
                regCode: lead.regCode ?? undefined,
                leadId: lead.id,
                source: "manual",
              });
            }}
          >
            <Phone className="size-4" />
            {t("common.call")}
          </button>
        ) : null}
        {!lead.convertedClientId ? (
          <button type="button" className="crm-mi" onClick={() => void doConvert()}>
            <ArrowRight className="size-4" />
            {t("leads.convert")}
          </button>
        ) : null}
        <div className="crm-menu-sep" />
        <button
          type="button"
          className="crm-mi crm-mi-danger"
          onClick={() => {
            close();
            setConfirmDel(true);
          }}
        >
          <Trash2 className="size-4" />
          {t("leads.delete")}
        </button>
      </Pop>

      {confirmDel ? (
        <ConfirmModal
          title={t("leads.deleteTitle")}
          body={t("leads.deleteConfirm", { name: lead.name })}
          confirmLabel={t("common.delete")}
          cancelLabel={t("common.cancel")}
          onConfirm={doDelete}
          onClose={() => setConfirmDel(false)}
        />
      ) : null}
    </span>
  );
}
